import {
  Ruler,
  ShieldCheck,
  Cog,
  Clock,
  Wrench,
  FileText,
} from "lucide-react";
import { motion } from "framer-motion";
import { CountUp } from "@/components/CountUp";
import { COMPANY } from "@/lib/site-data";
import { DotGrid, FloatingShape, NumberWatermark, DiagonalLines } from "@/components/Deco";

const ITEMS = [
  {
    icon: Ruler,
    title: "Egyedi méretre gyártás",
    text: "Pontos méretvétel, bármilyen forma és kiegészítő – az Ön igényeire szabva.",
  },
  {
    icon: ShieldCheck,
    title: "Tartós, időjárásálló anyag",
    text: "Belga, német és osztrák PVC ponyva, UV- és vízálló kivitelben, hosszú élettartammal.",
  },
  {
    icon: Cog,
    title: "Precíz kivitelezés",
    text: "Hegesztett varratok, megerősített sarkok, profi rögzítés – minőség, ami kitart.",
  },
  {
    icon: Clock,
    title: "Gyors javítás",
    text: "Szakadás, lyuk, fül vagy heveder – időpontfoglalás nélkül, érkezési sorrendben.",
  },
  {
    icon: Wrench,
    title: "Tapasztalt szakemberek",
    text: "Több, mint 20 év szakmai múlt és több ezer elégedett megrendelő áll mögöttünk.",
  },
  {
    icon: FileText,
    title: "Ingyenes ajánlatkérés",
    text: "Kötelezettségmentes árajánlat – telefonon, e-mailen vagy az űrlapunkon keresztül.",
  },
];

export const WhyUs = () => {
  return (
    <section
      id="miert-minket"
      data-testid="why-us-section"
      className="section-pad bg-[#F8FAFC] border-y border-gray-200 relative overflow-hidden"
    >
      <FloatingShape className="-top-20 -right-20 w-96 h-96" color="#1E3E62" />
      <DotGrid className="absolute bottom-10 left-10 w-72 h-32 opacity-50" size={16} color="rgba(30,62,98,0.4)" />
      <NumberWatermark n="02" className="hidden md:block top-8 -left-12" />
      <DiagonalLines className="absolute top-0 left-0 right-0 h-2 text-[#EA580C]" opacity={0.4} />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <span className="eyebrow">Miért minket válasszon?</span>
            <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Megbízhatóság minden öltésben.
            </h2>
            <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed">
              Évtizedek óta gyártunk és javítunk ponyvákat egyéni
              megrendelőknek és vállalatoknak. Ügyfeleink visszatérnek hozzánk,
              mert minden munkánk mögött a precizitás és a tartósság áll.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <CountUp
                  end={COMPANY.experienceYears}
                  suffix="+"
                  className="font-heading font-extrabold text-[#EA580C] text-4xl block"
                />
                <div className="text-xs sm:text-sm text-gray-500 mt-1">
                  év szakmai tapasztalat
                </div>
              </div>
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <CountUp
                  end={5000}
                  suffix="+"
                  className="font-heading font-extrabold text-[#EA580C] text-4xl block"
                />
                <div className="text-xs sm:text-sm text-gray-500 mt-1">
                  legyártott ponyva
                </div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ITEMS.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                data-testid={`why-item-${item.title.replace(/\s+/g, "-").toLowerCase()}`}
                className="group bg-white p-6 border-l-4 border-[#EA580C] rounded-md shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-md bg-[#EA580C]/10 flex items-center justify-center text-[#EA580C] group-hover:bg-[#EA580C] group-hover:text-white transition-colors">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-heading font-bold text-[#0B192C] text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
